import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { IConfig, NgxMaskDirective, provideNgxMask } from "ngx-mask";

// Angular Material
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatExpansionModule } from "@angular/material/expansion";
import { AngularMaterialModule } from "../modules/angular-material/angular-material.module";

// Components
import { ClientAutoCompleteComponent } from "./client-auto-complete/client-auto-complete.component";
import { ClientAutoCompleteSalesComponent } from "./client-auto-complete-sales/client-auto-complete-sales.component";
import { VehicleBrandAutoCompleteComponent } from "./vehicle-brand-auto-complete/vehicle-brand-auto-complete.component";
import { VehicleBrandAutoCompletePartsComponent } from "./vehicle-brand-auto-complete-parts/vehicle-brand-auto-complete.component";
import { VehicleModelAutoCompleteComponent } from "./vehicle-model-auto-complete/vehicle-model-auto-complete.component";
import { VehicleModelAutoCompletePartsComponent } from "./vehicle-model-auto-complete-parts/vehicle-model-auto-complete.component";
import { VehicleAutoCompleteComponent } from "./vehicle-auto-complete/vehicle-auto-complete.component";
import { VehiclePlateCompleteComponent } from "./vehicle-plate-complete/vehicle-plate-complete.component";
import { PartAutoCompleteComponent } from "./part-auto-complete/part-auto-complete.component";
import { PartAutoCompletePartsComponent } from "./part-auto-complete-parts/part-auto-complete.component";
import { PartManufacturerAutoCompleteComponent } from "./part-manufacturer-auto-complete/part-manufacturer-auto-complete.component";
import { PartManufacturePartcodeAutoComplete } from "./part-manufacture-partcode-auto-complete/part-manufacture-partcode-auto-complete";
import { PartManufacturerDriveBAutoCompleteComponent } from "./part-manufacturer-driveb-auto-complete/part-manufacturer-driveb-auto-complete.component";
import { PartCategoryAutoCompleteComponent } from "./part-category-auto-complete/part-category-auto-complete.component";
import { MeasurementUnitAutoCompleteComponent } from "./measurement-unit-auto-complete/measurement-unit-auto-complete.component";
import { ServiceAutoCompleteComponent } from "./service-auto-complete/service-auto-complete.component";
import { ServicesAutoCompleteComponent } from "./services-auto-complete/services-auto-complete.component";
import { CarWorkshopAutoCompleteComponent } from "./car-workshop-auto-complete/car-workshop-auto-complete.component";
import { DriverAutoCompleteComponent } from "./driver-auto-complete/driver-auto-complete.component";
import { CountryAutoCompleteComponent } from "./country-auto-complete/country-auto-complete.component";
import { StateAutoCompleteComponent } from "./state-auto-complete/state-auto-complete.component";
import { CityAutoCompleteComponent } from "./city-auto-complete/city-auto-complete.component";
import { PriceTableModelAutoCompleteComponent } from "./price-table-model-auto-complete/price-table-model-auto-complete.component";
import { FranchiseAutoCompleteComponent } from "./franchise-auto-complete/franchise-auto-complete/franchise-auto-complete.component";
import { AccordionSearchComponent } from "./accordion-search/accordion-search.component";
import { BudgetPartItemFormModalComponent } from "./budget-part-item-form-modal/budget-part-item-form-modal.component";
import { MessageBeforeManuallyAddPartModalComponent } from "./message-before-manually-add-part-modal/message-before-manually-add-part-modal.component";
import { MessageBeforeManuallyAddPartWarningUserModalComponent } from "./message-before-manually-add-part-warning-user-modal/message-before-manually-add-part-warning-user-modal.component";
import { BudgetServiceItemFormModalComponent } from "./budget-service-item-form-modal/budget-service-item-form-modal.component";
import { BlockAutoCompleteComponent } from "./block-auto-complete/block-auto-complete.component";
import { KmMaskDirective } from "./kmMask/km-mask.directive";
import { GenericAutocompleteComponent } from "./generic-autocomplete/generic-autocomplete.component";

// Config Mask
const maskConfig: Partial<IConfig> = {
  validation: false
};

@NgModule({
  declarations: [
    // Autocompletes
    GenericAutocompleteComponent,
    ClientAutoCompleteComponent,
    ClientAutoCompleteSalesComponent,
    VehicleBrandAutoCompleteComponent,
    VehicleBrandAutoCompletePartsComponent,
    VehicleModelAutoCompleteComponent,
    VehicleModelAutoCompletePartsComponent,
    VehicleAutoCompleteComponent,
    VehiclePlateCompleteComponent,
    PartAutoCompleteComponent,
    PartAutoCompletePartsComponent,
    PartManufacturerAutoCompleteComponent,
    PartManufacturePartcodeAutoComplete,
    PartManufacturerDriveBAutoCompleteComponent,
    PartCategoryAutoCompleteComponent,
    MeasurementUnitAutoCompleteComponent,
    ServiceAutoCompleteComponent,
    ServicesAutoCompleteComponent,
    CarWorkshopAutoCompleteComponent,
    DriverAutoCompleteComponent,
    CountryAutoCompleteComponent,
    StateAutoCompleteComponent,
    CityAutoCompleteComponent,
    PriceTableModelAutoCompleteComponent,
    AccordionSearchComponent,
    BlockAutoCompleteComponent,
    FranchiseAutoCompleteComponent,

    // Directives
    KmMaskDirective,

    // Modals
    BudgetPartItemFormModalComponent,
    MessageBeforeManuallyAddPartModalComponent,
    MessageBeforeManuallyAddPartWarningUserModalComponent,
    BudgetServiceItemFormModalComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatAutocompleteModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatGridListModule,
    MatExpansionModule,
    AngularMaterialModule,
    NgxMaskDirective
  ],
  exports: [
    // Components
    GenericAutocompleteComponent,
    ClientAutoCompleteComponent,
    ClientAutoCompleteSalesComponent,
    VehicleBrandAutoCompleteComponent,
    VehicleBrandAutoCompletePartsComponent,
    VehicleModelAutoCompleteComponent,
    VehicleModelAutoCompletePartsComponent,
    VehicleAutoCompleteComponent,
    VehiclePlateCompleteComponent,
    PartAutoCompleteComponent,
    PartAutoCompletePartsComponent,
    PartManufacturerAutoCompleteComponent,
    PartManufacturePartcodeAutoComplete,
    PartManufacturerDriveBAutoCompleteComponent,
    PartCategoryAutoCompleteComponent,
    MeasurementUnitAutoCompleteComponent,
    ServiceAutoCompleteComponent,
    ServicesAutoCompleteComponent,
    CarWorkshopAutoCompleteComponent,
    DriverAutoCompleteComponent,
    CountryAutoCompleteComponent,
    StateAutoCompleteComponent,
    CityAutoCompleteComponent,
    PriceTableModelAutoCompleteComponent,
    FranchiseAutoCompleteComponent,
    AccordionSearchComponent,
    BlockAutoCompleteComponent,

    // Directives
    KmMaskDirective
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [provideNgxMask(maskConfig)]
})
export class WidgetModule {}
